import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { GetPromotions, GetProducts } from "../../../requests";
import styles from "./styles.module.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[0-9+\-\s()]{7,20}$/;
const SAVED_TESTS_KEY = "promotionLabSavedTests";

const DEFAULT_CUSTOMER = {
  email: "qa@crework.in",
  phone: "",
};

const SIZE_OPTIONS = ["small", "medium", "large"];

const createCartItem = (overrides = {}) => ({
  rowId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  productId: "",
  productName: "",
  size: "medium",
  quantity: "1",
  price: "",
  ...overrides,
});

const pickFirstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const firstNumber = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === "") {
      continue;
    }

    const numberValue = Number(value);
    if (Number.isFinite(numberValue)) {
      return numberValue;
    }
  }

  return 0;
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const getProductId = (product) => product?.id || product?._id || product?.name || "";

const getCouponId = (coupon) => coupon?.id || coupon?._id || coupon?.code || "";

const getCouponCode = (coupon) => coupon?.code || coupon?.couponCode || "";

const getArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(toNumber(value));

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatTime = (date = new Date()) =>
  new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);

const sanitizeQuantity = (value) => value.replace(/[^\d]/g, "");

const sanitizePrice = (value) => {
  const cleanedValue = value.replace(/[^0-9.]/g, "");
  const [whole, ...decimalParts] = cleanedValue.split(".");
  return decimalParts.length ? `${whole}.${decimalParts.join("")}` : cleanedValue;
};

const getAvailableSizes = (product) => {
  if (!product?.pizzaPrices) {
    return ["regular"];
  }

  const sizes = SIZE_OPTIONS.filter((size) => product.pizzaPrices?.[size]);
  return sizes.length ? sizes : ["regular"];
};

const getDefaultSize = (product) => {
  const sizes = getAvailableSizes(product);
  return sizes.includes("medium") ? "medium" : sizes[0];
};

const getProductPrice = (product, size = "medium") => {
  if (product?.pizzaPrices && product.pizzaPrices[size]) {
    return firstNumber(
      product.pizzaPrices[size]?.discountPrice,
      product.pizzaPrices[size]?.price
    );
  }

  return firstNumber(product?.discountPrice, product?.price);
};

const displayValue = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return value;
};

const getResponseBody = (rawResponse) => rawResponse?.data || rawResponse || {};

const getCouponTypeLabel = (type) => {
  const labels = {
    buyXgetY: "Buy X Get Y",
    cheapestFree: "Cheapest Free",
    combo: "Combo",
    firstOrder: "First Order",
    fixed: "Flat Discount",
    flat: "Flat Discount",
    freeDelivery: "Free Delivery",
    percent: "Percentage",
    percentage: "Percentage",
    spendGet: "Spend Get",
  };

  return labels[type] || displayValue(type, "-");
};

const getStrategyName = (coupon) => {
  const strategies = {
    buyXgetY: "BuyXGetYStrategy",
    cheapestFree: "CheapestFreeStrategy",
    combo: "ComboStrategy",
    firstOrder: "FirstOrderStrategy",
    fixed: "FlatStrategy",
    flat: "FlatStrategy",
    freeDelivery: "FreeDeliveryStrategy",
    percent: "PercentageStrategy",
    percentage: "PercentageStrategy",
    spendGet: "SpendGetStrategy",
  };

  return strategies[coupon?.discountType] || "BaseStrategy";
};

const formatItemsList = (items, products) => {
  const arr = getArray(items);
  if (!arr.length) return "";
  
  const productLookup = getArray(products).reduce((acc, p) => {
    const id = p.productId || p.id || p._id;
    if (id) acc[String(id)] = p.name;
    return acc;
  }, {});

  return arr.map(item => {
    if (item && typeof item === 'object') {
      const pName = productLookup[String(item.productId)] || item.productId || 'Unknown';
      const vName = item.variantId && item.variantId !== 'any' ? ` (${item.variantId})` : '';
      return `${pName}${vName}`;
    }
    return String(item);
  }).join(', ');
};

const getCouponBuyText = (coupon, products) => {
  const quantity = coupon?.xValue || coupon?.minBuyQuantity || "-";
  const buyItems = getArray(coupon?.buyItems);
  const formatted = formatItemsList(buyItems, products);
  return `${quantity} ${formatted || "eligible item(s)"}`;
};

const getCouponRewardText = (coupon, products) => {
  const freeItems = getArray(coupon?.freeItems);

  if (coupon?.discountType === "percent") {
    return `${displayValue(coupon?.discountPercent, 0)}% off`;
  }

  if (coupon?.discountType === "fixed") {
    return `${formatCurrency(coupon?.discountAmount)} off`;
  }

  if (freeItems.length) {
    return `${coupon?.yValue || 1} ${formatItemsList(freeItems, products)}`;
  }

  return displayValue(coupon?.description, "-");
};

const getRepeatText = (coupon) => {
  if (coupon?.repeatMode) {
    return coupon.repeatMode;
  }

  return coupon?.usage === "single" ? "Single use" : "Unlimited";
};

const resolveSummaryValue = (rawResponse, selectedCoupon, field, products) => {
  const responseBody = getResponseBody(rawResponse);
  const promotion = responseBody?.promotion || responseBody?.appliedPromotion || {};
  const reward = responseBody?.reward || {};

  switch (field) {
    case "promotionType":
      return pickFirstPresent(
        responseBody?.promotionType,
        responseBody?.type,
        promotion?.promotionType,
        promotion?.type,
        selectedCoupon ? getCouponTypeLabel(selectedCoupon.discountType) : ""
      );
    case "reward":
      return pickFirstPresent(
        reward?.message,
        responseBody?.reward,
        responseBody?.rewardDescription,
        responseBody?.message,
        selectedCoupon ? getCouponRewardText(selectedCoupon, products) : ""
      );
    case "discount":
      return pickFirstPresent(
        reward?.discountAmount,
        responseBody?.discount,
        responseBody?.discountAmount,
        responseBody?.discountValue,
        responseBody?.totalDiscount,
        selectedCoupon?.discountAmount
      );
    case "status":
      return pickFirstPresent(
        responseBody?.status,
        responseBody?.state,
        responseBody?.result,
        responseBody?.success === true ? "Pass" : "",
        responseBody?.success === false ? "Fail" : "",
        selectedCoupon?.status === "enable" ? "Enabled" : selectedCoupon?.status
      );
    default:
      return "";
  }
};

const resolveSeverity = (rawResponse) => {
  const responseBody = getResponseBody(rawResponse);
  const statusText = String(
    pickFirstPresent(responseBody?.status, responseBody?.state, responseBody?.result, "")
  ).toLowerCase();

  if (responseBody?.success === false || /error|fail|invalid|denied|reject/.test(statusText)) {
    return "error";
  }

  if (/warn|partial|expired/.test(statusText)) {
    return "warning";
  }

  if (responseBody?.success === true || /success|applied|eligible|valid|pass/.test(statusText)) {
    return "success";
  }

  return "info";
};

const resolveResultMessage = (rawResponse) => {
  const responseBody = getResponseBody(rawResponse);

  return displayValue(
    pickFirstPresent(
      responseBody?.reward?.message,
      responseBody?.message,
      responseBody?.error,
      responseBody?.reason,
      responseBody?.description,
      "Promotion evaluation completed."
    )
  );
};

const getAlertColor = (severity) => {
  const colors = {
    error: "danger",
    info: "info",
    success: "success",
    warning: "warning",
  };

  return colors[severity] || "info";
};

const getMatchableProducts = (coupon) => {
  const items = getArray(coupon?.buyItems);
  return items.map(item => {
    if (item && typeof item === "object") {
      return String(item.productId || "").toLowerCase().trim();
    }
    return String(item).toLowerCase().trim();
  });
};

const productMatchesCoupon = (product, coupon) => {
  const buyItems = getMatchableProducts(coupon);
  if (!buyItems.length) {
    return true;
  }
  const pName = normalize(product?.name);
  const pId = String(product?.productId || product?.id || product?._id || "").toLowerCase().trim();
  return buyItems.includes(pName) || buyItems.includes(pId);
};

const findProductForNames = (products, items) => {
  const list = getArray(items);
  const targetIds = list.map(item => {
    if (item && typeof item === "object") {
      return String(item.productId || "").toLowerCase().trim();
    }
    return String(item).toLowerCase().trim();
  });

  if (!targetIds.length) {
    return (
      products.find((product) => product?.pizzaPrices || normalize(product?.category) === "pizza") ||
      products[0]
    );
  }

  return (
    products.find((product) => {
      const pName = normalize(product.name);
      const pId = String(product.productId || product.id || product._id || "").toLowerCase().trim();
      return targetIds.includes(pName) || targetIds.includes(pId);
    }) ||
    products.find((product) => product?.pizzaPrices || normalize(product?.category) === "pizza") ||
    products[0]
  );
};

const createItemFromProduct = (product, quantity = 1, preferredSize = "medium") => {
  if (!product) {
    return createCartItem({ quantity: String(quantity) });
  }

  const sizes = getAvailableSizes(product);
  const size = sizes.includes(preferredSize) ? preferredSize : getDefaultSize(product);

  return createCartItem({
    productId: getProductId(product),
    productName: product.name || "",
    size,
    quantity: String(quantity),
    price: String(getProductPrice(product, size)),
  });
};

const getCartDiagnostics = (cartItems, productLookup, selectedCoupon, rawResponse) => {
  const responseBody = getResponseBody(rawResponse);
  const qualification = responseBody?.qualification || {};

  if (qualification.unit) {
    return {
      qualified: !!qualification.qualified,
      matched: qualification.found,
      required: qualification.required,
      needed: qualification.remaining,
      unit: qualification.unit,
      label: qualification.label,
    };
  }

  // Client-side fallback before evaluation response is received:
  const discountType = selectedCoupon?.discountType;
  let unit = "currency";
  let label = "Spend Amount";
  let required = 0;
  let matched = 0;

  if (discountType === "buyXgetY" || selectedCoupon?.xValue) {
    unit = "quantity";
    label = "Pizza Quantity";
    required = toNumber(selectedCoupon?.xValue || 2);
    const buyItems = getMatchableProducts(selectedCoupon);
    matched = cartItems.reduce((total, item) => {
      const product = productLookup[item.productId] || {};
      const productName = normalize(item.productName || product.name);
      const productId = String(item.productId || "").toLowerCase().trim();
      const matches = !buyItems.length || buyItems.includes(productName) || buyItems.includes(productId);
      return matches ? total + toNumber(item.quantity) : total;
    }, 0);
  } else if (discountType === "firstOrder" || selectedCoupon?.firstOrderOnly) {
    unit = "boolean";
    label = "First Order";
    required = true;
    matched = true; // assume true until backend validates otherwise
  } else if (discountType === "freeDelivery") {
    unit = "currency";
    label = "Spend Amount";
    required = toNumber(selectedCoupon?.minimumOrder || 0);
    matched = cartItems.reduce((sum, item) => sum + (toNumber(item.price) * toNumber(item.quantity)), 0);
  } else {
    required = toNumber(selectedCoupon?.minimumOrder || 0);
    matched = cartItems.reduce((sum, item) => sum + (toNumber(item.price) * toNumber(item.quantity)), 0);
  }

  const qualified = unit === "boolean" ? matched === required : (required === 0 ? matched > 0 : matched >= required);

  return {
    qualified,
    matched,
    required,
    needed: unit === "boolean" ? null : Math.max(required - matched, 0),
    unit,
    label,
  };
};

const getTimelineRows = (rawResponse) => {
  const responseBody = getResponseBody(rawResponse);
  const diagnostics = responseBody?.diagnostics || {};
  const timing = diagnostics?.timing || {};
  const timeline = diagnostics?.timeline || {};

  return [
    ["Request", timeline.requestMs],
    ["Validator", timeline.validatorMs],
    ["Repository", timeline.repositoryMs],
    ["Engine", timeline.engineMs],
    ["Strategy", timeline.strategyMs],
    ["Reward", timeline.rewardMs],
    ["Response", timing.durationMs],
  ];
};

const PromotionLab = () => {
  document.title = "Promotion Lab | Susipizza";

  const userRole = useSelector((state) => state?.login?.role);
  const [couponCode, setCouponCode] = useState("");
  const [selectedCouponId, setSelectedCouponId] = useState("");
  const [couponSearch, setCouponSearch] = useState("");
  const [email, setEmail] = useState(DEFAULT_CUSTOMER.email);
  const [phone, setPhone] = useState(DEFAULT_CUSTOMER.phone);
  const [cartItems, setCartItems] = useState([createCartItem()]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [menuError, setMenuError] = useState("");
  const [couponError, setCouponError] = useState("");
  const [errors, setErrors] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);
  const [result, setResult] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [scenarioName, setScenarioName] = useState("");
  const [showFirestore, setShowFirestore] = useState(false);
  const [testName, setTestName] = useState("");
  const [savedTests, setSavedTests] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_TESTS_KEY)) || [];
    } catch {
      return [];
    }
  });

  const productLookup = useMemo(
    () =>
      products.reduce((lookup, product) => {
        lookup[getProductId(product)] = product;
        return lookup;
      }, {}),
    [products]
  );

  const couponLookup = useMemo(
    () =>
      coupons.reduce((lookup, coupon) => {
        lookup[getCouponId(coupon)] = coupon;
        return lookup;
      }, {}),
    [coupons]
  );

  const selectedCoupon = selectedCouponId ? couponLookup[selectedCouponId] : null;

  const filteredCoupons = useMemo(() => {
    const search = normalize(couponSearch);
    if (!search) {
      return coupons;
    }

    return coupons.filter((coupon) => {
      const code = normalize(getCouponCode(coupon));
      const heading = normalize(coupon.heading);
      const type = normalize(coupon.discountType);
      return code.includes(search) || heading.includes(search) || type.includes(search);
    });
  }, [couponSearch, coupons]);

  const cartTotal = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + toNumber(item.quantity) * toNumber(item.price),
        0
      ),
    [cartItems]
  );

  const cartDiagnostics = useMemo(
    () => getCartDiagnostics(cartItems, productLookup, selectedCoupon, rawResponse),
    [cartItems, productLookup, rawResponse, selectedCoupon]
  );

  const jsonResponse = useMemo(
    () => JSON.stringify(rawResponse || {}, null, 2),
    [rawResponse]
  );

  const firestoreSnapshot = useMemo(() => {
    if (!selectedCoupon) {
      return {};
    }

    return {
      id: getCouponId(selectedCoupon),
      code: getCouponCode(selectedCoupon),
      discountType: selectedCoupon.discountType,
      buyItems: selectedCoupon.buyItems || [],
      freeItems: selectedCoupon.freeItems || [],
      xValue: selectedCoupon.xValue,
      yValue: selectedCoupon.yValue,
      minimumOrder: selectedCoupon.minimumOrder,
      maximumDiscount: selectedCoupon.maximumDiscount,
      repeatMode: selectedCoupon.repeatMode || selectedCoupon.usage,
      usage: selectedCoupon.usage,
      status: selectedCoupon.status,
      expiryDate: selectedCoupon.expiryDate,
    };
  }, [selectedCoupon]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setMenuError("");

        const response = await GetProducts({});
        const menuItems = response?.data?.data || response?.data || [];
        setProducts(Array.isArray(menuItems) ? menuItems : []);
      } catch (error) {
        setMenuError("Menu items could not be loaded.");
      } finally {
        setProductsLoading(false);
      }
    };

    const fetchCoupons = async () => {
      try {
        setCouponsLoading(true);
        setCouponError("");

        const token = localStorage.getItem("token");
        const response = await GetPromotions(token);
        const couponItems = response?.data || response || [];
        setCoupons(Array.isArray(couponItems) ? couponItems : []);
      } catch (error) {
        setCouponError("Promotions could not be loaded.");
      } finally {
        setCouponsLoading(false);
      }
    };

    fetchProducts();
    fetchCoupons();
  }, []);

  useEffect(() => {
    localStorage.setItem(SAVED_TESTS_KEY, JSON.stringify(savedTests));
  }, [savedTests]);

  if (userRole !== "superadmin") {
    return <Navigate to="/dashboard" replace />;
  }

  const updateCartItem = (rowId, updates) => {
    setCartItems((currentItems) =>
      currentItems.map((item) =>
        item.rowId === rowId ? { ...item, ...updates } : item
      )
    );
  };

  const handleCouponSelect = (couponId) => {
    const coupon = couponLookup[couponId];

    setSelectedCouponId(couponId);
    setCouponCode(coupon ? getCouponCode(coupon) : "");
    setRawResponse(null);
    setResult(null);
    setShowFirestore(false);
  };

  const handleProductChange = (rowId, productId) => {
    const product = productLookup[productId];
    const size = product ? getDefaultSize(product) : "medium";

    updateCartItem(rowId, {
      productId: product ? getProductId(product) : "",
      productName: product?.name || "",
      size,
      price: product ? String(getProductPrice(product, size)) : "",
    });
  };

  const handleSizeChange = (rowId, size) => {
    const item = cartItems.find((cartItem) => cartItem.rowId === rowId);
    const product = productLookup[item?.productId];

    updateCartItem(rowId, {
      size,
      price: product ? String(getProductPrice(product, size)) : item?.price || "",
    });
  };

  const handleAddItem = () => {
    setCartItems((currentItems) => [...currentItems, createCartItem()]);
  };

  const handleDeleteItem = (rowId) => {
    if (cartItems.length === 1) {
      return;
    }

    setCartItems((currentItems) => currentItems.filter((item) => item.rowId !== rowId));
  };

  const hydrateCartForCoupon = (quantityOverride) => {
    const requiredQuantity = quantityOverride || selectedCoupon?.xValue || 2;
    const product = findProductForNames(products, selectedCoupon?.buyItems);
    setCartItems([createItemFromProduct(product, requiredQuantity, "medium")]);
  };

  const applyScenario = (scenario) => {
    setScenarioName(scenario.label);
    setEmail(DEFAULT_CUSTOMER.email);
    setPhone(DEFAULT_CUSTOMER.phone);
    setRawResponse(null);
    setResult(null);

    if (scenario.key === "invalid") {
      setSelectedCouponId("");
      setCouponCode("INVALID-CODE");
      const fallbackProduct = products[0];
      setCartItems([createItemFromProduct(fallbackProduct, 1, "medium")]);
      return;
    }

    if (!selectedCoupon && coupons.length) {
      const coupon =
        scenario.key === "expired"
          ? coupons.find((item) => new Date(item.expiryDate) < new Date())
          : coupons[0];

      if (coupon) {
        setSelectedCouponId(getCouponId(coupon));
        setCouponCode(getCouponCode(coupon));
      }
    }

    const activeCoupon = selectedCoupon || coupons[0];
    const product = findProductForNames(products, activeCoupon?.buyItems);
    const xValue = activeCoupon?.xValue || 2;

    const quantities = {
      buy1: 1,
      buy2: 2,
      buy4: 4,
      cheapestFree: xValue + 1,
      expired: xValue,
      minimumOrder: 1,
    };

    setCartItems([createItemFromProduct(product, quantities[scenario.key] || xValue, "medium")]);
  };

  const runSavedTest = (test) => {
    setScenarioName(test.name);
    setSelectedCouponId(test.selectedCouponId);
    setCouponCode(test.couponCode);
    setEmail(test.email);
    setPhone(test.phone);
    setCartItems(test.cartItems.map((item) => createCartItem(item)));
    setRawResponse(null);
    setResult(null);
  };

  const handleSaveTest = () => {
    const trimmedName = testName.trim();
    if (!trimmedName) {
      return;
    }

    const nextTest = {
      id: `${Date.now()}`,
      name: trimmedName,
      selectedCouponId,
      couponCode,
      email,
      phone,
      cartItems,
    };

    setSavedTests((currentTests) => [nextTest, ...currentTests].slice(0, 8));
    setTestName("");
  };

  const validateForm = () => {
    const nextErrors = {
      cartItems: {},
    };

    if (!couponCode.trim()) {
      nextErrors.couponCode = "Coupon code is required.";
    }

    if (!email.trim()) {
      nextErrors.email = "Customer email is required.";
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (phone.trim() && !PHONE_PATTERN.test(phone.trim())) {
      nextErrors.phone = "Enter a valid phone number.";
    }

    cartItems.forEach((item) => {
      const rowErrors = {};
      const quantity = toNumber(item.quantity);
      const price = toNumber(item.price);

      if (!item.productId) {
        rowErrors.product = "Select a product.";
      }

      if (!item.size) {
        rowErrors.size = "Select a size.";
      }

      if (!item.quantity || quantity <= 0) {
        rowErrors.quantity = "Quantity must be greater than 0.";
      }

      if (item.price === "" || price < 0) {
        rowErrors.price = "Price must be 0 or greater.";
      }

      if (Object.keys(rowErrors).length > 0) {
        nextErrors.cartItems[item.rowId] = rowErrors;
      }
    });

    if (Object.keys(nextErrors.cartItems).length === 0) {
      delete nextErrors.cartItems;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    couponCode: couponCode.trim(),
    customer: {
      email: email.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
    },
    cartItems: cartItems.map((item) => {
      const product = productLookup[item.productId] || {};
      const quantity = toNumber(item.quantity);
      const price = toNumber(item.price);

      return {
        productId: item.productId,
        productName: item.productName || product.name || "",
        name: item.productName || product.name || "",
        size: item.size,
        quantity,
        price,
        subtotal: quantity * price,
      };
    }),
  });

  const handleEvaluate = async () => {
    if (!validateForm()) {
      setResult({
        severity: "warning",
        title: "Validation required",
        message: "Please fix the highlighted fields before evaluating.",
      });
      return;
    }

    try {
      setIsEvaluating(true);
      setResult(null);

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/promotion/evaluate`,
        buildPayload(),
        token ? { headers: { Authorization: token } } : undefined
      );
      const responseData = response?.data || response;
      const severity = resolveSeverity(responseData);

      setRawResponse(responseData);
      setResult({
        severity,
        title: "Evaluation result",
        message: resolveResultMessage(responseData),
      });
      setTestHistory((history) =>
        [
          {
            id: `${Date.now()}`,
            time: formatTime(),
            status: severity === "success" ? "PASS" : "FAIL",
            couponCode: couponCode.trim(),
            scenarioName,
          },
          ...history,
        ].slice(0, 8)
      );
    } catch (error) {
      const errorResponse = error?.response?.data || error?.message || error;
      const normalizedError =
        typeof errorResponse === "string" ? { message: errorResponse } : errorResponse;

      setRawResponse(normalizedError);
      setResult({
        severity: "error",
        title: "Evaluation failed",
        message: resolveResultMessage(normalizedError),
      });
      setTestHistory((history) =>
        [
          {
            id: `${Date.now()}`,
            time: formatTime(),
            status: "FAIL",
            couponCode: couponCode.trim(),
            scenarioName,
          },
          ...history,
        ].slice(0, 8)
      );
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleReset = () => {
    setCouponCode("");
    setSelectedCouponId("");
    setCouponSearch("");
    setEmail(DEFAULT_CUSTOMER.email);
    setPhone(DEFAULT_CUSTOMER.phone);
    setCartItems([createCartItem()]);
    setErrors({});
    setRawResponse(null);
    setResult(null);
    setScenarioName("");
    setShowFirestore(false);
  };

  const diagnostics = getResponseBody(rawResponse)?.diagnostics || {};
  const promotion = getResponseBody(rawResponse)?.promotion || {};
  const qualification = getResponseBody(rawResponse)?.qualification || {};
  const reward = getResponseBody(rawResponse)?.reward || {};
  const timelineRows = getTimelineRows(rawResponse);

  const scenarios = [
    { key: "buy2", label: "Buy 2 Pizza" },
    { key: "buy4", label: "Buy 4 Pizza" },
    { key: "buy1", label: "Buy 1 Pizza" },
    { key: "expired", label: "Expired Coupon" },
    { key: "invalid", label: "Invalid Coupon" },
    { key: "minimumOrder", label: "Minimum Order" },
    { key: "cheapestFree", label: "Cheapest Free" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Promotion Lab" />

          <div className={styles.header}>
            <h4 className={styles.title}>Promotion Lab</h4>
            <p className={styles.subtitle}>Internal testing tool for Promotion Engine</p>
          </div>

          {(menuError || couponError) && (
            <Alert color="warning" className={styles.alert}>
              {[menuError, couponError].filter(Boolean).join(" ")}
            </Alert>
          )}

          <Row>
            <Col xl={8}>
              <Row>
                <Col xl={6}>
                  <Card className={styles.card}>
                    <CardBody>
                      <div className={styles.cardHeader}>
                        <CardTitle tag="h5" className={styles.cardTitle}>
                          Coupon
                        </CardTitle>
                        {couponsLoading && <Spinner size="sm" color="primary" />}
                      </div>

                      <FormGroup>
                        <Label for="promotion-coupon-search">Search Coupon</Label>
                        <Input
                          id="promotion-coupon-search"
                          type="text"
                          value={couponSearch}
                          onChange={(event) => setCouponSearch(event.target.value)}
                          placeholder="BUY2GET1, 20FLAT, FREECOKE"
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label for="promotion-coupon-code">Select</Label>
                        <Input
                          id="promotion-coupon-code"
                          type="select"
                          value={selectedCouponId}
                          invalid={Boolean(errors.couponCode)}
                          disabled={couponsLoading}
                          onChange={(event) => handleCouponSelect(event.target.value)}
                        >
                          <option value="">Select Coupon</option>
                          {filteredCoupons.map((coupon) => (
                            <option key={getCouponId(coupon)} value={getCouponId(coupon)}>
                              {getCouponCode(coupon)}
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{errors.couponCode}</FormFeedback>
                      </FormGroup>

                      <FormGroup>
                        <Label for="promotion-coupon-code-manual">Coupon Code</Label>
                        <Input
                          id="promotion-coupon-code-manual"
                          type="text"
                          value={couponCode}
                          invalid={Boolean(errors.couponCode)}
                          onChange={(event) => {
                            setSelectedCouponId("");
                            setCouponCode(event.target.value.toUpperCase());
                          }}
                        />
                        <FormFeedback>{errors.couponCode}</FormFeedback>
                      </FormGroup>

                      <div className={styles.inlineActions}>
                        <Button
                          color="primary"
                          outline
                          size="sm"
                          disabled={!selectedCoupon || productsLoading}
                          onClick={() => hydrateCartForCoupon()}
                        >
                          <i className="mdi mdi-auto-fix me-1" />
                          Generate Test Cart
                        </Button>
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          disabled={!selectedCoupon}
                          onClick={() => setShowFirestore((value) => !value)}
                        >
                          <i className="mdi mdi-database-eye-outline me-1" />
                          View Firestore
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={6}>
                  <Card className={styles.card}>
                    <CardBody>
                      <CardTitle tag="h5" className={styles.cardTitle}>
                        Customer
                      </CardTitle>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="promotion-customer-email">Email</Label>
                            <Input
                              id="promotion-customer-email"
                              type="email"
                              value={email}
                              invalid={Boolean(errors.email)}
                              onChange={(event) => setEmail(event.target.value)}
                            />
                            <FormFeedback>{errors.email}</FormFeedback>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="promotion-customer-phone">Phone</Label>
                            <Input
                              id="promotion-customer-phone"
                              type="text"
                              value={phone}
                              invalid={Boolean(errors.phone)}
                              onChange={(event) => setPhone(event.target.value)}
                            />
                            <FormFeedback>{errors.phone}</FormFeedback>
                            {!errors.phone && <small className="text-muted">Optional</small>}
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className={styles.card}>
                <CardBody>
                  <CardTitle tag="h5" className={styles.cardTitle}>
                    Promotion Snapshot
                  </CardTitle>
                  <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                      <span>Promotion Type</span>
                      <strong>{selectedCoupon ? getCouponTypeLabel(selectedCoupon.discountType) : "-"}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Buy</span>
                      <strong>{selectedCoupon ? getCouponBuyText(selectedCoupon, products) : "-"}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Reward</span>
                      <strong>{selectedCoupon ? getCouponRewardText(selectedCoupon, products) : "-"}</strong>
                    </div>
                    {selectedCoupon && (selectedCoupon.discountType === "buyXgetY" || selectedCoupon.reward?.type === "FREE_ITEM" || selectedCoupon.reward?.type === "FREE_PRODUCT") && (
                      <div className={styles.summaryItem}>
                        <span>Reward Policy</span>
                        <strong>{displayValue(selectedCoupon.policy?.rewardPolicy || selectedCoupon.policy?.selection, "Fixed Product")}</strong>
                      </div>
                    )}
                    <div className={styles.summaryItem}>
                      <span>Status</span>
                      <strong>
                        <Badge color={selectedCoupon?.status === "enable" ? "success" : "secondary"} pill>
                          {selectedCoupon ? displayValue(selectedCoupon.status) : "None"}
                        </Badge>
                      </strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Expiry</span>
                      <strong>{selectedCoupon ? formatDate(selectedCoupon.expiryDate) : "-"}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Repeat</span>
                      <strong>{selectedCoupon ? getRepeatText(selectedCoupon) : "-"}</strong>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className={styles.card}>
                <CardBody>
                  <div className={styles.cardHeader}>
                    <CardTitle tag="h5" className={styles.cardTitle}>
                      Cart
                    </CardTitle>
                    <Button color="primary" outline onClick={handleAddItem}>
                      <i className="mdi mdi-plus me-1" />
                      Add Item
                    </Button>
                  </div>

                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className={styles.productCell}>Product</th>
                          <th className={styles.sizeCell}>Size</th>
                          <th className={styles.numberCell}>Quantity</th>
                          <th className={styles.numberCell}>Price</th>
                          <th className={styles.numberCell}>Subtotal</th>
                          <th className={styles.actionCell}>Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartItems.map((item) => {
                          const rowErrors = errors.cartItems?.[item.rowId] || {};
                          const product = productLookup[item.productId];
                          const sizes = product ? getAvailableSizes(product) : ["medium"];
                          const subtotal = toNumber(item.quantity) * toNumber(item.price);

                          return (
                            <tr key={item.rowId}>
                              <td className={styles.productCell}>
                                <Input
                                  type="select"
                                  value={item.productId}
                                  invalid={Boolean(rowErrors.product)}
                                  disabled={productsLoading}
                                  onChange={(event) =>
                                    handleProductChange(item.rowId, event.target.value)
                                  }
                                >
                                  <option value="">
                                    {productsLoading ? "Loading products..." : "Select Product"}
                                  </option>
                                  {products.map((productItem) => (
                                    <option
                                      key={getProductId(productItem)}
                                      value={getProductId(productItem)}
                                    >
                                      {productItem.name}
                                    </option>
                                  ))}
                                </Input>
                                <FormFeedback>{rowErrors.product}</FormFeedback>
                              </td>
                              <td className={styles.sizeCell}>
                                <Input
                                  type="select"
                                  value={item.size}
                                  invalid={Boolean(rowErrors.size)}
                                  onChange={(event) =>
                                    handleSizeChange(item.rowId, event.target.value)
                                  }
                                >
                                  {sizes.map((size) => (
                                    <option key={size} value={size}>
                                      {size.charAt(0).toUpperCase() + size.slice(1)}
                                    </option>
                                  ))}
                                </Input>
                                <FormFeedback>{rowErrors.size}</FormFeedback>
                              </td>
                              <td className={styles.numberCell}>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  value={item.quantity}
                                  invalid={Boolean(rowErrors.quantity)}
                                  onChange={(event) =>
                                    updateCartItem(item.rowId, {
                                      quantity: sanitizeQuantity(event.target.value),
                                    })
                                  }
                                />
                                <FormFeedback>{rowErrors.quantity}</FormFeedback>
                              </td>
                              <td className={styles.numberCell}>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={item.price}
                                  invalid={Boolean(rowErrors.price)}
                                  onChange={(event) =>
                                    updateCartItem(item.rowId, {
                                      price: sanitizePrice(event.target.value),
                                    })
                                  }
                                />
                                <FormFeedback>{rowErrors.price}</FormFeedback>
                              </td>
                              <td className={styles.numberCell}>
                                <strong>{formatCurrency(subtotal)}</strong>
                              </td>
                              <td className={styles.actionCell}>
                                <Button
                                  color="danger"
                                  outline
                                  size="sm"
                                  disabled={cartItems.length === 1}
                                  onClick={() => handleDeleteItem(item.rowId)}
                                  title="Delete item"
                                >
                                  <i className="mdi mdi-delete" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>

              <Row>
                <Col xl={6}>
                  <Card className={styles.card}>
                    <CardBody>
                      <CardTitle tag="h5" className={styles.cardTitle}>
                        Cart Visualizer
                      </CardTitle>
                      <div
                        className={`${styles.visualState} ${
                          cartDiagnostics.qualified ? styles.visualSuccess : styles.visualDanger
                        }`}
                      >
                        <Badge color={cartDiagnostics.qualified ? "success" : "danger"} pill>
                          {cartDiagnostics.qualified ? "Qualified" : "Not Qualified"}
                        </Badge>
                        <strong>{selectedCoupon ? getCouponCode(selectedCoupon) : couponCode || "-"}</strong>
                      </div>
                      <div className={styles.visualList}>
                        {cartItems.map((item) => (
                          <div className={styles.visualRow} key={item.rowId}>
                            <div>
                              <strong>{displayValue(item.productName, "No product")}</strong>
                              <span>{item.size} / Qty {displayValue(item.quantity, 0)}</span>
                            </div>
                            <Badge
                              color={
                                productMatchesCoupon(productLookup[item.productId], selectedCoupon)
                                  ? "success"
                                  : "secondary"
                              }
                              pill
                            >
                              Match
                            </Badge>
                          </div>
                        ))}
                      </div>
                      <div className={styles.needBox}>
                        {(!cartDiagnostics.unit || cartDiagnostics.unit === "currency") && (
                          <>
                            <span>Qualification</span>
                            <strong>{cartDiagnostics.label || "Spend Amount"}</strong>
                            <span>Required</span>
                            <strong>{formatCurrency(cartDiagnostics.required)}</strong>
                            <span>Current Cart</span>
                            <strong>{formatCurrency(cartDiagnostics.matched)}</strong>
                            <span>Remaining</span>
                            <strong>{formatCurrency(cartDiagnostics.needed)}</strong>
                            <span>Status</span>
                            <strong>{cartDiagnostics.qualified ? "Qualified" : "Not Qualified"}</strong>
                          </>
                        )}
                        {cartDiagnostics.unit === "quantity" && (
                          <>
                            <span>Qualification</span>
                            <strong>{cartDiagnostics.label || "Pizza Quantity"}</strong>
                            <span>Required</span>
                            <strong>{cartDiagnostics.required}</strong>
                            <span>Found</span>
                            <strong>{cartDiagnostics.matched}</strong>
                            <span>Remaining</span>
                            <strong>{cartDiagnostics.needed}</strong>
                            <span>Status</span>
                            <strong>{cartDiagnostics.qualified ? "Qualified" : "Not Qualified"}</strong>
                          </>
                        )}
                        {cartDiagnostics.unit === "boolean" && (
                          <>
                            <span>Qualification</span>
                            <strong>{cartDiagnostics.label}</strong>
                            <span>Customer Status</span>
                            <strong>
                              {cartDiagnostics.label === "First Order"
                                ? (cartDiagnostics.matched ? "First Order" : "Not First Order")
                                : (cartDiagnostics.matched ? "Qualified" : "Not Qualified")}
                            </strong>
                            <span>Status</span>
                            <strong>{cartDiagnostics.qualified ? "Qualified" : "Not Qualified"}</strong>
                          </>
                        )}
                      </div>
                      <div className={styles.rewardBox}>
                        <span>Reward</span>
                        <strong>
                          {reward?.freeItems?.length
                            ? reward.freeItems
                                .map((item) => `${item.quantity} ${item.name}`)
                                .join(", ")
                            : selectedCoupon
                            ? getCouponRewardText(selectedCoupon, products)
                            : "-"}
                        </strong>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={6}>
                  <Card className={styles.card}>
                    <CardBody>
                      <CardTitle tag="h5" className={styles.cardTitle}>
                        Diagnostics
                      </CardTitle>
                      <div className={styles.diagnosticsGrid}>
                        <div>
                          <span>Promotion</span>
                          <strong>{displayValue(promotion.code || getCouponCode(selectedCoupon))}</strong>
                        </div>
                        <div>
                          <span>Strategy</span>
                          <strong>{displayValue(diagnostics?.strategy?.strategy || getStrategyName(selectedCoupon))}</strong>
                        </div>
                        {((promotion && (promotion.type === "FREE_ITEM" || promotion.type === "FREE_PRODUCT")) || (selectedCoupon && (selectedCoupon.discountType === "buyXgetY" || selectedCoupon.reward?.type === "FREE_ITEM" || selectedCoupon.reward?.type === "FREE_PRODUCT"))) && (
                          <div>
                            <span>Reward Policy</span>
                            <strong>{displayValue(diagnostics?.policy?.rewardPolicy || selectedCoupon?.policy?.rewardPolicy || selectedCoupon?.policy?.selection, "Fixed Product")}</strong>
                          </div>
                        )}
                        <div>
                          <span>Qualified</span>
                          <strong>{cartDiagnostics.qualified ? "YES" : "NO"}</strong>
                        </div>
                        <div>
                          <span>Matched</span>
                          <strong>{displayValue(qualification.matched, cartDiagnostics.matched)}</strong>
                        </div>
                        <div>
                          <span>Required</span>
                          <strong>{displayValue(qualification.required, cartDiagnostics.required)}</strong>
                        </div>
                        <div>
                          <span>Repeat</span>
                          <strong>{displayValue(diagnostics?.strategy?.repeatCount, 0)}</strong>
                        </div>
                        <div>
                          <span>Rule</span>
                          <strong>{displayValue(diagnostics?.validation?.code)}</strong>
                        </div>
                        <div>
                          <span>Execution</span>
                          <strong>{displayValue(diagnostics?.timing?.durationMs, "-")} ms</strong>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className={styles.card}>
                <CardBody>
                  <CardTitle tag="h5" className={styles.cardTitle}>
                    Summary
                  </CardTitle>
                  <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                      <span>Cart Total</span>
                      <strong>{formatCurrency(cartTotal)}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Promotion Type</span>
                      <strong>{displayValue(resolveSummaryValue(rawResponse, selectedCoupon, "promotionType", products))}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Reward</span>
                      <strong>{displayValue(resolveSummaryValue(rawResponse, selectedCoupon, "reward", products))}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Discount</span>
                      <strong>{formatCurrency(resolveSummaryValue(rawResponse, selectedCoupon, "discount", products))}</strong>
                    </div>
                    <div className={styles.summaryItem}>
                      <span>Status</span>
                      <strong>
                        <Badge color={result?.severity === "error" ? "danger" : "primary"} pill>
                          {displayValue(resolveSummaryValue(rawResponse, selectedCoupon, "status", products), "Ready")}
                        </Badge>
                      </strong>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <div className={styles.actions}>
                <Button color="primary" disabled={isEvaluating} onClick={handleEvaluate}>
                  {isEvaluating ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="mdi mdi-flask-outline me-1" />
                  )}
                  {isEvaluating ? "Evaluating..." : "Evaluate Promotion"}
                </Button>
                <Button color="secondary" outline disabled={isEvaluating} onClick={handleReset}>
                  <i className="mdi mdi-refresh me-1" />
                  Reset
                </Button>
              </div>

              {result && (
                <Alert color={getAlertColor(result.severity)} className={styles.alert}>
                  <h5 className="alert-heading mb-1">{result.title}</h5>
                  <div>{result.message}</div>
                </Alert>
              )}

              <Row>
                <Col xl={6}>
                  <Card className={styles.card}>
                    <CardBody>
                      <CardTitle tag="h5" className={styles.cardTitle}>
                        Execution Timeline
                      </CardTitle>
                      <div className={styles.timeline}>
                        {timelineRows.map(([label, duration]) => (
                          <div className={styles.timelineRow} key={label}>
                            <span>{label}</span>
                            <strong>{duration === undefined ? "-" : `${duration} ms`}</strong>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={6}>
                  <Card className={styles.card}>
                    <CardBody>
                      <div className={styles.cardHeader}>
                        <CardTitle tag="h5" className={styles.cardTitle}>
                          Firestore Snapshot
                        </CardTitle>
                        <Badge color={showFirestore ? "primary" : "light"} className={showFirestore ? "" : "text-dark"}>
                          Coupon
                        </Badge>
                      </div>
                      <pre className={styles.snapshotViewer}>
                        {showFirestore ? JSON.stringify(firestoreSnapshot, null, 2) : "{}"}
                      </pre>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className={styles.card}>
                <CardBody>
                  <div className={styles.cardHeader}>
                    <CardTitle tag="h5" className={styles.cardTitle}>
                      JSON Viewer
                    </CardTitle>
                    <Badge color="light" className="text-dark">
                      Raw API response
                    </Badge>
                  </div>
                  <pre className={styles.jsonViewer}>{jsonResponse}</pre>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className={styles.card}>
                <CardBody>
                  <CardTitle tag="h5" className={styles.cardTitle}>
                    Saved Test Cases
                  </CardTitle>
                  <div className={styles.scenarioList}>
                    {scenarios.map((scenario) => (
                      <button
                        type="button"
                        key={scenario.key}
                        className={`${styles.scenarioButton} ${
                          scenarioName === scenario.label ? styles.scenarioActive : ""
                        }`}
                        onClick={() => applyScenario(scenario)}
                      >
                        <span>{scenario.label}</span>
                        <Badge color="light" className="text-dark">
                          Run
                        </Badge>
                      </button>
                    ))}
                  </div>
                </CardBody>
              </Card>

              <Card className={styles.card}>
                <CardBody>
                  <CardTitle tag="h5" className={styles.cardTitle}>
                    Save Test
                  </CardTitle>
                  <FormGroup>
                    <Label for="promotion-save-test">Test Name</Label>
                    <Input
                      id="promotion-save-test"
                      type="text"
                      value={testName}
                      onChange={(event) => setTestName(event.target.value)}
                      placeholder="Buy2Pizza"
                    />
                  </FormGroup>
                  <Button color="primary" outline block onClick={handleSaveTest}>
                    Save
                  </Button>
                  <div className={styles.savedList}>
                    {savedTests.map((test) => (
                      <button
                        type="button"
                        key={test.id}
                        className={styles.savedButton}
                        onClick={() => runSavedTest(test)}
                      >
                        <span>{test.name}</span>
                        <small>{test.couponCode}</small>
                      </button>
                    ))}
                    {!savedTests.length && <div className={styles.emptyState}>No saved tests</div>}
                  </div>
                </CardBody>
              </Card>

              <Card className={styles.card}>
                <CardBody>
                  <CardTitle tag="h5" className={styles.cardTitle}>
                    Test History
                  </CardTitle>
                  <div className={styles.historyList}>
                    {testHistory.map((entry) => (
                      <div className={styles.historyItem} key={entry.id}>
                        <span>{entry.time}</span>
                        <Badge color={entry.status === "PASS" ? "success" : "danger"} pill>
                          {entry.status}
                        </Badge>
                        <strong>{entry.couponCode}</strong>
                        <small>{entry.scenarioName || "Manual"}</small>
                      </div>
                    ))}
                    {!testHistory.length && <div className={styles.emptyState}>No test history</div>}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PromotionLab;
